/**
 * Table — sortable columns, cursor pagination, row actions. Roadmap 4.2.
 *
 * Generic over the row type and driven by a column list, because the same table
 * appears in four portals: courses, students, orders, tickets, moderation
 * queues. A hand-rolled `<table>` per screen is four chances to forget the
 * sortable header's `aria-sort` and the empty state.
 *
 * **Sorting and paging are the caller's job.** The server does both (§7.1:
 * `?ordering=`, `?cursor=`), so this renders the controls and reports the
 * intent. A table that sorted its own current page would silently sort *part* of
 * the data.
 */
import { ArrowDown, ArrowUp, ChevronLeft, ChevronRight, ChevronsUpDown } from 'lucide-react'

import { useLocale } from '@/shared/i18n/useLocale'

import { Button } from './Button'
import { cn } from './cn'
import { SkeletonRows } from './states'

export type SortDirection = 'asc' | 'desc'

export type Sort = { column: string; direction: SortDirection }

export type Column<Row> = {
  /** Stable key. Also the `?ordering=` value when `sortable`. */
  key: string
  header: string
  /** Cell contents. Anything, so a cell can hold a badge or a menu. */
  cell: (row: Row) => React.ReactNode
  sortable?: boolean | undefined
  /** Numeric columns align to the inline end in both directions. */
  numeric?: boolean | undefined
  className?: string | undefined
  /** Hide below `sm`. Some columns are noise on a phone, not content. */
  hideOnMobile?: boolean | undefined
  /**
   * Absorb the leftover width. Exactly one column should claim it — the one
   * holding the row's identity.
   *
   * Without it every column shares the table evenly, which put a hand's width of
   * nothing between a course title and its status while the counts drifted into
   * the middle of the row. `w-full` on one cell and `whitespace-nowrap` on the
   * rest is what makes the others shrink to their content.
   */
  primary?: boolean | undefined
}

export type TableProps<Row> = {
  caption: string
  columns: readonly Column<Row>[]
  rows: readonly Row[]
  rowKey: (row: Row) => string
  loading?: boolean | undefined
  loadingLabel: string
  /** Shown in place of the table body when there are no rows. */
  empty?: React.ReactNode
  sort?: Sort | undefined
  onSortChange?: ((sort: Sort) => void) | undefined
  /** Per-row menu, rendered in a final column with no header. */
  rowActions?: ((row: Row) => React.ReactNode) | undefined
  actionsLabel?: string | undefined
  onRowClick?: ((row: Row) => void) | undefined
  pagination?: PaginationProps | undefined
  className?: string | undefined
}

export function Table<Row>({
  caption,
  columns,
  rows,
  rowKey,
  loading = false,
  loadingLabel,
  empty,
  sort,
  onSortChange,
  rowActions,
  actionsLabel,
  onRowClick,
  pagination,
  className,
}: TableProps<Row>) {
  if (loading) return <SkeletonRows label={loadingLabel} />
  if (rows.length === 0 && empty) return <>{empty}</>

  return (
    <div className={cn('space-y-4', className)}>
      {/* The wrapper scrolls, not the page: a wide table in a narrow viewport
          must not make the whole document scroll sideways. */}
      <div className="overflow-x-auto rounded-xl border border-line">
        <table className="w-full border-collapse text-sm">
          <caption className="sr-only">{caption}</caption>
          <thead className="bg-sunken">
            <tr>
              {columns.map((column) => (
                <HeaderCell
                  key={column.key}
                  column={column}
                  sort={sort}
                  onSortChange={onSortChange}
                />
              ))}
              {rowActions ? (
                <th scope="col" className="w-12 px-3 py-2">
                  <span className="sr-only">{actionsLabel}</span>
                </th>
              ) : null}
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => (
              <tr
                key={rowKey(row)}
                onClick={onRowClick ? () => onRowClick(row) : undefined}
                className={cn(
                  'border-t border-line',
                  onRowClick && 'cursor-pointer hover:bg-sunken',
                )}
              >
                {columns.map((column) => (
                  <td
                    key={column.key}
                    className={cn(
                      'px-3 py-3 align-middle',
                      // `w-full` on one cell and nowrap on the rest is what makes
                      // the others shrink to their content — see `primary`.
                      column.primary ? 'w-full' : 'whitespace-nowrap',
                      // `text-end`, never `text-right` — a number column has to
                      // align to the same edge the reading direction ends on.
                      column.numeric && 'text-end tabular-nums',
                      column.hideOnMobile && 'hidden sm:table-cell',
                      column.className,
                    )}
                  >
                    {column.cell(row)}
                  </td>
                ))}
                {rowActions ? (
                  <td
                    className="px-3 py-3 align-middle"
                    // The menu inside must not also trigger the row.
                    onClick={(event) => event.stopPropagation()}
                  >
                    {rowActions(row)}
                  </td>
                ) : null}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {pagination ? <Pagination {...pagination} /> : null}
    </div>
  )
}

function HeaderCell<Row>({
  column,
  sort,
  onSortChange,
}: {
  column: Column<Row>
  sort?: Sort | undefined
  onSortChange?: ((sort: Sort) => void) | undefined
}) {
  const active = sort?.column === column.key
  const sortable = column.sortable && onSortChange

  return (
    <th
      scope="col"
      // `aria-sort` is how a screen reader announces the current order. Without
      // it a sorted table is indistinguishable from an unsorted one.
      aria-sort={active ? (sort?.direction === 'asc' ? 'ascending' : 'descending') : undefined}
      className={cn(
        'px-3 py-2.5 text-start text-xs font-semibold tracking-caps text-fg-muted',
        column.primary ? 'w-full' : 'whitespace-nowrap',
        column.numeric && 'text-end',
        column.hideOnMobile && 'hidden sm:table-cell',
      )}
    >
      {sortable ? (
        <button
          type="button"
          onClick={() =>
            onSortChange({
              column: column.key,
              direction: active && sort?.direction === 'asc' ? 'desc' : 'asc',
            })
          }
          // `uppercase` is repeated here, not just on the `th`: Tailwind's
          // preflight sets `text-transform: none` on `button`, so a sortable
          // header would sit in sentence case beside its uppercase neighbours.
          className="inline-flex items-center gap-1 uppercase hover:text-fg"
        >
          {column.header}
          {active ? (
            sort?.direction === 'asc' ? (
              <ArrowUp aria-hidden="true" className="size-3" />
            ) : (
              <ArrowDown aria-hidden="true" className="size-3" />
            )
          ) : (
            <ChevronsUpDown aria-hidden="true" className="size-3 opacity-40" />
          )}
        </button>
      ) : (
        column.header
      )}
    </th>
  )
}

// ---------------------------------------------------------------------------
export type PaginationProps = {
  previousLabel: string
  nextLabel: string
  /** Absent means there is no page in that direction. */
  onPrevious?: (() => void) | undefined
  onNext?: (() => void) | undefined
  /** e.g. "12 courses" — cursor pagination has no page numbers to show. */
  summary?: string | undefined
}

/**
 * Previous and next only.
 *
 * The API uses cursor pagination (§7.1), which has no page count by design —
 * `COUNT(*)` on every list request is what it buys. Numbered pages would be a
 * lie about data the client does not have.
 */
export function Pagination({
  previousLabel,
  nextLabel,
  onPrevious,
  onNext,
  summary,
}: PaginationProps) {
  const { direction } = useLocale()
  // Directional icons mirror — §12.3. "Previous" points back along the reading
  // direction, which is rightwards in Arabic.
  const Back = direction === 'rtl' ? ChevronRight : ChevronLeft
  const Forward = direction === 'rtl' ? ChevronLeft : ChevronRight

  return (
    <div className="flex flex-wrap items-center gap-3">
      {summary ? <p className="text-sm text-fg-muted">{summary}</p> : null}
      <div className="ms-auto flex items-center gap-2">
        <Button variant="secondary" size="sm" onClick={onPrevious} disabled={!onPrevious}>
          <Back aria-hidden="true" />
          {previousLabel}
        </Button>
        <Button variant="secondary" size="sm" onClick={onNext} disabled={!onNext}>
          {nextLabel}
          <Forward aria-hidden="true" />
        </Button>
      </div>
    </div>
  )
}
